<!--
 * Path: packages/frontend/src/features/browser/forms/relations/RelationsEdit.vue
 * Task: Generic write component for single-select relations strongly typing activeEntity properties using reactive props.
 * Refactoring: Removed manual entityType overrides so EntityIcon uses the natively resolved item types from useSelectOptions.
-->
<template>
  <div class="d-flex align-center mb-6">
    <v-select
      v-if="activeEntity"
      v-model="(activeEntity as any)[props.field.id]"
      :label="props.field.name"
      :items="getSelectOptions(props.field.entitySource || '', props.field.id)"
      item-title="title"
      item-value="id"
      variant="outlined"
      density="compact"
      clearable
      hide-details
      class="flex-grow-1"
      :disabled="props.field.readonly"
    >
      <template v-slot:item="{ props: slotProps, item }">
        <v-list-item v-bind="slotProps" :title="item.raw.title">
          <template v-slot:prepend>
            <EntityIcon :entity="item.raw" size="24" class="me-3" />
          </template>
        </v-list-item>
      </template>
      <template v-slot:selection="{ item }">
        <div class="d-flex align-center">
          <EntityIcon :entity="item.raw" size="24" class="me-3" />
          <span>{{ item.raw.title }}</span>
        </div>
      </template>
    </v-select>
    <v-tooltip v-if="props.field.info" location="end">
      <template v-slot:activator="{ props: tooltipProps }">
        <v-btn v-bind="tooltipProps" icon="mdi-information-outline" variant="text" size="x-small" class="ms-1 flex-grow-0" @click="$emit('show-info')"></v-btn>
      </template>
      <span>{{ props.field.info }}</span>
    </v-tooltip>
  </div>
</template>

<script setup lang="ts">
import { computed, inject } from 'vue';
import { useWorkspaceStore } from '@/store/workspace';
import { useSelectOptions } from '@/core/composables/useSelectOptions';
import { FormContextKey } from '@/core/keys';
import type { Entity } from '@/core/types/editor';
import EntityIcon from '@/core/components/common/EntityIcon.vue';

export interface FieldProps {
  id: string;
  name: string;
  info?: string;
  entitySource?: string;
  readonly?: boolean;
}

const props = defineProps<{ field: FieldProps }>();
defineEmits<{ (e: 'show-info'): void; }>();

const workspaceStore = useWorkspaceStore();
const { getSelectOptions } = useSelectOptions();
const formContext = inject(FormContextKey, null);
const activeEntity = computed<Entity | null>(() => formContext ? formContext.entity.value : workspaceStore.selectedEntity);
</script>
